import React, { useRef } from "react";
import { useKeyDown } from "../../hooks/useKeyDown";
import { Client, EMPTY_CLIENT } from "../../models/client";
import { InfoTab } from "./client-setup-tabs";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M1 7h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ClientSetupForm({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const [client, setClient] = React.useState<Client | null>(null);
  const ref = useRef(null);

  useKeyDown(() => {
    if (ref.current === document.activeElement) save();
  }, ["Enter"]);

  React.useEffect(() => {
    if (clientId && !client) {
      if (clientId === "new") {
        setClient(EMPTY_CLIENT);
      } else {
        fetchClient();
      }
    }
  }, [clientId, client]);

  const fetchClient = async () => {
    const snapshot = await getDoc(doc(getFirestore(), "clients", clientId));
    if (snapshot.exists()) setClient({ ...snapshot.data(), id: clientId } as Client);
  };

  const save = async () => {
    if (clientId === "new") {
      await addDoc(collection(getFirestore(), "clients"), { ...client, timeCreated: new Date() });
      toast.success("Client setup complete!", { position: "top-right", autoClose: 5000, hideProgressBar: true, closeOnClick: true, pauseOnHover: false, draggable: false });
      navigate(`/client/new`);
      setClient(EMPTY_CLIENT);
    } else {
      await updateDoc(doc(getFirestore(), "clients", clientId), { ...client });
      toast.success("Client updated!", { position: "top-right", autoClose: 5000, hideProgressBar: true, closeOnClick: true, pauseOnHover: false, draggable: false });
    }
  };

  if (!client) return <div />;

  return (
    <div className="p-content p-setup-page">
      <div className="p-pagehead">
        <div>
          <div className="p-breadcrumb">Workspace · Clients</div>
          <h1 className="p-pagetitle">Client Setup</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="p-btn p-btn-ghost" onClick={() => navigate("/admin/clients")}>Cancel</button>
          <div tabIndex={0} ref={ref}>
            <button className="p-btn p-btn-primary" onClick={save}>
              Save &amp; complete <ArrowRight />
            </button>
          </div>
        </div>
      </div>

      <div className="p-card">
        <div style={{ padding: 32 }}>
          <InfoTab client={client} setClient={setClient} showRequiredErrors={false} />
        </div>
      </div>
    </div>
  );
}

export default ClientSetupForm;
